import React, { useState, useEffect } from "react";
import { useContext } from "react";
import { DataContext } from "../DataContext";
import { useAuth0 } from "@auth0/auth0-react";
import { deleteCart } from "../api/cart";
import { createOrder } from "../api/order";
import CheckoutProduct from "../components/CheckoutProduct";
import { PageTemplate, HeaderTemplate } from "../styles";
import styled from "styled-components";
import toast from "react-hot-toast";

const Card = styled.div`
  margin-bottom: 20px;
  &.products {
    margin-bottom: 0;
    color: var(--dark-grey);
    overflow: hidden;
    height: 100%;
    .success:last-child {
      border-bottom: 0;
      box-shadow: 0;
    }
    .success-products {
      .success .flex-col {
        align-items: flex-start;
      }
    }
    .product-review {
      padding: 30px 0;
      .total {
        display: flex;
        justify-content: space-between;
        align-items: center;
        span {
          font-weight: 700;
          &.value {
            text-align: right;
          }
        }
        &.shipping,
        &.salestax,
        &.discount {
          margin-bottom: 10px;
        }
      }
      & > div {
        position: relative;
        span {
          z-index: 1;
          background-color: var(--white);
          padding-right: 10px;
          &.value {
            padding-left: 10px;
            padding-right: 0;
          }
        }
      }
      & > div::after {
        border: 1px dashed #dddddd;
        content: "";
        top: 50%;
        width: 100%;
        height: 1px;
        right: 0;
        display: block;
        position: absolute;
        z-index: 0;
      }
    }
  }
  .card-info {
    margin-top: 10px;
    font-weight: 700;
    font-style: italic;
  }
  h2 {
    span {
      margin-left: 20px;
    }
    @media only screen and (max-width: 767px) {
      font-size: 18px;
    }
  }
`;

function Success() {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { productsInCart, clearCartState, getCustomer } = useContext(DataContext);
  const [cartData, setCartData] = useState(null);
  const [order, setOrder] = useState(null);

  // get query string parameter from url
  const urlParams = new URLSearchParams(window.location.search);
  const paymentIntentId = urlParams.get("payment_intent");

  // component mount
  useEffect(() => {
    setCartData(productsInCart);
  }, []);

  useEffect(() => {
    if (cartData) {
      const fetchOrder = async () => {
        try {
          const checkoutToken = sessionStorage.getItem(`gg-checkout-${paymentIntentId}`);
          if (!checkoutToken) throw new Error("Checkout authorization is missing. Please contact support if you were charged.");
          const response = await createOrder(user?.sub, cartData, paymentIntentId, checkoutToken);
          setOrder(response);
          // Keep the checkout token for authenticated confirmation-page refreshes.
          // sessionStorage is scoped to this browser tab and is cleared when it closes.
          const cleanupKey = `gg-checkout-completed-${paymentIntentId}`;
          if (sessionStorage.getItem(cleanupKey) === "1") return;
          sessionStorage.setItem(cleanupKey, "1");
          clearCartState();
          if (isAuthenticated && !isLoading) {
            const token = await getAccessTokenSilently();
            const headers = {
              "Content-Type": "application/json",
              authorization: `Bearer ${token}`,
            };
            deleteCart(user.sub, headers);
            getCustomer();
          }
        } catch (error) {
          console.error(error);
          toast.error(error.message);
        }
      };
      fetchOrder();
    }
  }, [cartData]);

  if (!order) return null;

  const shippingCosts = order.shippingRate / 100;
  const salesTax = order.salesTax / 100;
  const discount = order.discount ? order.discount / 100 : 0;

  return (
    <>
      <HeaderTemplate className="narrow">
        <div className="header">
          <div className="title">
            <h1>Thank You For Your Purchase!</h1>
          </div>
        </div>
      </HeaderTemplate>
      <PageTemplate className="narrow">
        <div className="body columns success">
          <div className="left-column">
            <Card className="products">
              <div className="success-products">
                {order.products.map((currentProduct, idx) => (
                  <CheckoutProduct success={true} key={idx} product={currentProduct}></CheckoutProduct>
                ))}
              </div>

              <div className="product-review">
                <div className="total shipping">
                  <span>Shipping</span>
                  <span className="value">${shippingCosts.toFixed(2)}</span>
                </div>
                {salesTax > 0 && (
                  <div className="total salestax">
                    <span>Sales Tax</span>
                    <span className="value">${salesTax.toFixed(2)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="total discount">
                    <span>Discount</span>
                    <span className="value">-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="total">
                  <span>Total</span>
                  <span className="value">${(order.total / 100).toFixed(2)}</span>
                </div>
              </div>
            </Card>
          </div>
          <div className="right-column">
            <Card>
              <h2>Order # {order.orderNumber}</h2>
              <div>{order.billing.email}</div>
            </Card>

            <Card>
              <h2>Shipping</h2>
              <div>{order.shipping.name}</div>
              <div>{order.shipping.address.line1}</div>
              <div>{order.shipping.address.line2}</div>
              <div>
                {order.shipping.address.city} {order.shipping.address.state} {order.shipping.address.postal_code}
              </div>
            </Card>

            <Card>
              <h2>Billing</h2>
              <div>{order.billing.name}</div>
              <div>{order.billing.address.line1}</div>
              <div>{order.billing.address.line2}</div>
              <div>
                {order.billing.address.city} {order.billing.address.state} {order.billing.address.postal_code}
              </div>
              <div className="card-info">
                {order.billing.card.brand} ({order.billing.card.last4})
              </div>
            </Card>
          </div>
        </div>
      </PageTemplate>
    </>
  );
}

export default Success;
